import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DollarSign,
  CreditCard,
  FileText,
  Clock,
  RefreshCcw,
  Shield,
  TrendingUp,
  Users,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Plus,
  Send,
  Eye,
  MoreHorizontal,
  Download,
  Receipt,
  Package,
  Sparkles,
  Info,
  ExternalLink,
  Zap,
  Gift,
  Calendar,
  Bell,
  Lock,
  Unlock,
  Settings,
  ChevronDown,
  ChevronUp,
  ToggleLeft,
  ToggleRight,
  Save,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { cn } from '../../lib/utils';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Current disclosure version - increment when disclosure text changes
const DISCLOSURE_VERSION = '1.0.0';

/**
 * Important Billing Disclaimer Component
 * Persistent informational notice shown in Overview, Collect Payment, and Compliance Disclosure
 */
const BillingDisclaimer = ({ acknowledged, onAcknowledge }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6" data-testid="billing-disclaimer">
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
        <AlertTriangle className="text-amber-600" size={20} />
      </div>
      <div className="flex-1">
        <h3 className="text-base font-semibold text-amber-800 mb-2">
          Important Billing Disclaimer
        </h3>
        <p className="text-xs text-amber-600 mb-3 italic">
          (Not legal advice. For informational purposes only.)
        </p>
        <div className="text-sm text-amber-700 space-y-2">
          <p>
            You are responsible for understanding and complying with all laws that apply to the 
            marketing, sale, and collection of fees for your credit repair services. You must 
            ensure that your marketing practices, payment collection methods, disclosures, and 
            client agreements comply with all applicable federal and state laws.
          </p>
          <p>
            If you use telemarketing or sales calls to close credit repair services over the phone, 
            you must also comply with the Telemarketing Sales Rule (TSR).
          </p>
          <p>
            You can learn more about the TSR through the{' '}
            <a 
              href="https://www.ftc.gov/business-guidance/resources/complying-telemarketing-sales-rule" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-amber-800 underline hover:text-amber-900 inline-flex items-center gap-1"
            >
              FTC <ExternalLink size={12} />
            </a>{' '}
            and related official compliance resources.
          </p>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Checkbox 
            id="disclaimer-ack" 
            checked={acknowledged}
            onCheckedChange={onAcknowledge}
          />
          <Label htmlFor="disclaimer-ack" className="text-sm text-amber-800 cursor-pointer">
            I understand and acknowledge this billing disclaimer.
          </Label>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Information Disclosure Modal
 * Required acknowledgment before first live payment collection
 */
const InformationDisclosureModal = ({ open, onClose, onAcknowledge, loading }) => {
  const [agreed, setAgreed] = useState(false);
  
  const handleAcknowledge = () => {
    if (agreed) {
      onAcknowledge();
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="information-disclosure-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Shield className="text-blue-600" size={24} />
            </div>
            <DialogTitle className="text-xl">Information Disclosure</DialogTitle>
          </div>
          <DialogDescription className="text-left">
            Required acknowledgment before processing client payments
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-700">
            To be fully transparent, the FundElite platform wants you to understand that collecting 
            payments for credit repair services may be subject to federal and state legal requirements.
          </p>
          
          <p className="text-sm text-slate-700">
            Before collecting payment from your customers, you should review and understand:
          </p>
          
          <ul className="list-disc list-inside text-sm text-slate-700 space-y-1 ml-2">
            <li>applicable state law</li>
            <li>the Credit Repair Organizations Act (CROA)</li>
            <li>the Telemarketing Sales Rule (TSR), if telemarketing or phone sales are involved</li>
          </ul>
          
          <p className="text-sm text-slate-700">
            By proceeding, you acknowledge that you are responsible for reviewing and complying 
            with the laws applicable to your business and payment practices.
          </p>
          
          <div className="bg-slate-50 rounded-lg p-4 mt-4">
            <div className="flex items-start gap-3">
              <Checkbox 
                id="disclosure-agree" 
                checked={agreed}
                onCheckedChange={setAgreed}
                data-testid="disclosure-checkbox"
              />
              <Label htmlFor="disclosure-agree" className="text-sm text-slate-800 cursor-pointer leading-relaxed">
                I understand and acknowledge this disclosure.
              </Label>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleAcknowledge} 
            disabled={!agreed || loading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="acknowledge-disclosure-btn"
          >
            {loading ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Processing...
              </>
            ) : (
              'Acknowledge & Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Client Billing Page - Main Component
 * 
 * Operational workspace for agency's client payment collection.
 * Separate from Billing & Membership (agency subscription to FundElite).
 */
const ClientBillingPage = () => {
  const { agencyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user } = useAuth();
  const outletContext = useOutletContext() || {};
  const { agency } = outletContext;
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [loading, setLoading] = useState(false);
  const [disclaimerAcknowledged, setDisclaimerAcknowledged] = useState(false);
  const [disclosureAcknowledged, setDisclosureAcknowledged] = useState(false);
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  const [acknowledgingDisclosure, setAcknowledgingDisclosure] = useState(false);
  const [acknowledgmentHistory, setAcknowledgmentHistory] = useState([]);
  
  // Self-Serve Offer / Trial Configuration State
  const [trialConfig, setTrialConfig] = useState({
    enabled: true,
    trial_length_days: 30,
    no_credit_card_required: true,
    auto_upgrade_enabled: false,
    default_paid_plan_id: null,
    trial_expiration_action: 'lock_access',
    max_disputes_per_month: 10,
    max_letters_per_month: 15,
    max_ai_analyses: 5,
    can_import_reports: true,
    can_use_credit_builder: true,
    can_access_blueprints: true,
    reminder_days: [7, 3, 1],
  });
  const [trialConfigLoading, setTrialConfigLoading] = useState(false);
  const [savingTrialConfig, setSavingTrialConfig] = useState(false);
  const [trialConfigExpanded, setTrialConfigExpanded] = useState({
    limits: false,
    reminders: false,
  });
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Fetch acknowledgment status on mount
  useEffect(() => {
    const fetchAcknowledgmentStatus = async () => {
      try {
        const response = await axios.get(
          `${API}/billing/agencies/${agencyId}/disclosure-status`,
          { headers }
        );
        setDisclosureAcknowledged(response.data?.acknowledged || false);
        setAcknowledgmentHistory(response.data?.history || []);
      } catch (err) {
        console.error('Failed to fetch acknowledgment status:', err);
      }
    };
    
    if (token && agencyId) {
      fetchAcknowledgmentStatus();
    }
  }, [token, agencyId]);
  
  // Fetch trial configuration when Self-Serve tab is active
  useEffect(() => {
    const fetchTrialConfig = async () => {
      if (activeTab !== 'selfserve') return;
      
      setTrialConfigLoading(true);
      try {
        const response = await axios.get(
          `${API}/selfserve/offers/${agencyId}/config`,
          { headers }
        );
        const autoRepairTrial = response.data?.offers?.auto_repair?.trial;
        if (autoRepairTrial) {
          setTrialConfig({
            enabled: autoRepairTrial.enabled ?? true,
            trial_length_days: autoRepairTrial.trial_length_days ?? 30,
            no_credit_card_required: autoRepairTrial.no_credit_card_required ?? true,
            auto_upgrade_enabled: autoRepairTrial.auto_upgrade_enabled ?? false,
            default_paid_plan_id: autoRepairTrial.default_paid_plan_id ?? null,
            trial_expiration_action: autoRepairTrial.trial_expiration_action ?? 'lock_access',
            max_disputes_per_month: autoRepairTrial.limits?.max_disputes_per_month ?? 10,
            max_letters_per_month: autoRepairTrial.limits?.max_letters_per_month ?? 15,
            max_ai_analyses: autoRepairTrial.limits?.max_ai_analyses ?? 5,
            can_import_reports: autoRepairTrial.limits?.can_import_reports ?? true,
            can_use_credit_builder: autoRepairTrial.limits?.can_use_credit_builder ?? true,
            can_access_blueprints: autoRepairTrial.limits?.can_access_blueprints ?? true,
            reminder_days: autoRepairTrial.reminder_schedule?.days_before_expiration ?? [7, 3, 1],
          });
        }
      } catch (err) {
        console.error('Failed to fetch trial config:', err);
      } finally {
        setTrialConfigLoading(false);
      }
    };
    
    if (token && agencyId) {
      fetchTrialConfig();
    }
  }, [token, agencyId, activeTab]);
  
  // Save trial configuration
  const handleSaveTrialConfig = async () => {
    setSavingTrialConfig(true);
    try {
      await axios.put(
        `${API}/selfserve/offers/${agencyId}/auto-repair/trial`,
        trialConfig,
        { headers }
      );
      toast.success('Trial configuration saved successfully');
    } catch (err) {
      console.error('Failed to save trial config:', err);
      toast.error('Failed to save trial configuration');
    } finally {
      setSavingTrialConfig(false);
    }
  };
  
  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };
  
  // Handle disclosure acknowledgment
  const handleAcknowledgeDisclosure = async () => {
    setAcknowledgingDisclosure(true);
    try {
      await axios.post(
        `${API}/billing/agencies/${agencyId}/acknowledge-disclosure`,
        { 
          disclosure_version: DISCLOSURE_VERSION,
          context: 'collect_payment',
        },
        { headers }
      );
      setDisclosureAcknowledged(true);
      setShowDisclosureModal(false);
      toast.success('Disclosure acknowledged successfully');
      
      // Refresh acknowledgment history
      const response = await axios.get(
        `${API}/billing/agencies/${agencyId}/disclosure-status`,
        { headers }
      );
      setAcknowledgmentHistory(response.data?.history || []);
    } catch (err) {
      console.error('Failed to acknowledge disclosure:', err);
      toast.error('Failed to acknowledge disclosure');
    } finally {
      setAcknowledgingDisclosure(false);
    }
  };
  
  // Check if payment collection is allowed (requires disclosure acknowledgment)
  const canCollectPayment = disclosureAcknowledged;
  
  // Handle collect payment action
  const handleCollectPaymentClick = () => {
    if (!disclosureAcknowledged) {
      setShowDisclosureModal(true);
    } else {
      // Proceed with payment collection
      toast.info('Payment collection flow would open here');
    }
  };
  
  // Tab definitions
  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'selfserve', label: 'Self-Serve Offers', icon: Zap },
    { id: 'services', label: 'Services & Pricing', icon: Package },
    { id: 'collect', label: 'Collect Payment', icon: CreditCard },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'history', label: 'Payment History', icon: Clock },
    { id: 'refunds', label: 'Refunds / Adjustments', icon: RefreshCcw },
    { id: 'compliance', label: 'Compliance Disclosure', icon: Shield },
  ];
  
  // Sample data for demo
  const stats = {
    collectedThisMonth: 12450,
    pendingInvoices: 3,
    overdueInvoices: 1,
    recentPayments: 8,
    failedPayments: 0,
  };
  
  const recentPayments = [
    { id: 1, client: 'John Smith', amount: 199, date: '2026-04-12', status: 'completed', service: 'Credit Repair - Month 1' },
    { id: 2, client: 'Maria Garcia', amount: 299, date: '2026-04-11', status: 'completed', service: 'Premium Package' },
    { id: 3, client: 'Robert Johnson', amount: 149, date: '2026-04-10', status: 'completed', service: 'Basic Plan' },
    { id: 4, client: 'Sarah Williams', amount: 99, date: '2026-04-09', status: 'pending', service: 'Setup Fee' },
  ];
  
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <BillingDisclaimer 
              acknowledged={disclaimerAcknowledged} 
              onAcknowledge={setDisclaimerAcknowledged} 
            />
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-500">Collected This Month</span>
                  <DollarSign className="text-emerald-500" size={18} />
                </div>
                <p className="text-2xl font-bold text-slate-900">${stats.collectedThisMonth.toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-500">Pending Invoices</span>
                  <Clock className="text-amber-500" size={18} />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats.pendingInvoices}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-500">Overdue</span>
                  <AlertTriangle className="text-red-500" size={18} />
                </div>
                <p className="text-2xl font-bold text-red-600">{stats.overdueInvoices}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-500">Recent Payments</span>
                  <CheckCircle2 className="text-emerald-500" size={18} />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats.recentPayments}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-500">Failed Payments</span>
                  <XCircle className="text-slate-400" size={18} />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats.failedPayments}</p>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleCollectPaymentClick} className="bg-emerald-600 hover:bg-emerald-700">
                  <CreditCard size={16} className="mr-2" />
                  Collect Payment
                </Button>
                <Button variant="outline" onClick={() => handleTabChange('invoices')}>
                  <FileText size={16} className="mr-2" />
                  Create Invoice
                </Button>
                <Button variant="outline" onClick={() => handleTabChange('services')}>
                  <Package size={16} className="mr-2" />
                  Manage Services
                </Button>
              </div>
            </div>
            
            {/* Recent Payments Table */}
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Recent Payments</h3>
                <Button variant="ghost" size="sm" onClick={() => handleTabChange('history')}>
                  View All <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
              <div className="divide-y divide-slate-100">
                {recentPayments.map((payment) => (
                  <div key={payment.id} className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Users size={18} className="text-slate-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{payment.client}</p>
                        <p className="text-xs text-slate-500">{payment.service}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-semibold text-slate-900">${payment.amount}</p>
                        <p className="text-xs text-slate-500">{payment.date}</p>
                      </div>
                      <Badge className={cn(
                        'capitalize',
                        payment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      )}>
                        {payment.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
        
      case 'selfserve':
        return (
          <div className="space-y-6" data-testid="selfserve-offers-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Self-Serve Offers</h2>
                <p className="text-sm text-slate-500">Configure customer-facing self-serve offers and trial settings</p>
              </div>
            </div>
            
            {/* Auto Repair Offer Card */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="auto-repair-offer-config">
              {/* Offer Header */}
              <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                      <Zap className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">Auto Repair Offer</h3>
                      <p className="text-sm text-slate-500">Self-service credit repair with AI-powered tools</p>
                    </div>
                  </div>
                  <Badge className={trialConfig.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                    {trialConfig.enabled ? 'Trial Enabled' : 'Trial Disabled'}
                  </Badge>
                </div>
              </div>
              
              {trialConfigLoading ? (
                <div className="p-8 flex items-center justify-center">
                  <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  {/* Free Trial Toggle */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Gift className="text-amber-600" size={20} />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Enable Free Trial</p>
                        <p className="text-sm text-slate-500">Allow customers to try before they buy</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setTrialConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
                      className="relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                      style={{ backgroundColor: trialConfig.enabled ? '#0FAC6C' : '#CBD5E1' }}
                      data-testid="enable-trial-toggle"
                    >
                      <div 
                        className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200"
                        style={{ transform: trialConfig.enabled ? 'translateX(30px)' : 'translateX(2px)' }}
                      />
                    </button>
                  </div>
                  
                  {trialConfig.enabled && (
                    <>
                      {/* Trial Length */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-700 font-medium">Trial Length (Days)</Label>
                          <div className="mt-2 flex items-center gap-3">
                            <Input
                              type="number"
                              value={trialConfig.trial_length_days}
                              onChange={(e) => setTrialConfig(prev => ({ ...prev, trial_length_days: parseInt(e.target.value) || 30 }))}
                              className="w-24"
                              min={1}
                              max={90}
                              data-testid="trial-length-input"
                            />
                            <span className="text-sm text-slate-500">days</span>
                            <div className="ml-auto flex gap-2">
                              {[7, 14, 30].map(days => (
                                <Button
                                  key={days}
                                  variant={trialConfig.trial_length_days === days ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setTrialConfig(prev => ({ ...prev, trial_length_days: days }))}
                                  className={trialConfig.trial_length_days === days ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                                >
                                  {days}
                                </Button>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-slate-700 font-medium">Trial Expiration Action</Label>
                          <Select 
                            value={trialConfig.trial_expiration_action}
                            onValueChange={(value) => setTrialConfig(prev => ({ ...prev, trial_expiration_action: value }))}
                          >
                            <SelectTrigger className="mt-2" data-testid="expiration-action-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="lock_access">Lock Access (Require Upgrade)</SelectItem>
                              <SelectItem value="downgrade_limited">Downgrade to Limited Access</SelectItem>
                              <SelectItem value="notify_only">Notify Only (Grace Period)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* No Credit Card Required Toggle */}
                      <div className="flex items-center justify-between p-4 rounded-xl border border-emerald-200 bg-emerald-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                            {trialConfig.no_credit_card_required ? (
                              <Unlock className="text-emerald-600" size={20} />
                            ) : (
                              <Lock className="text-emerald-600" size={20} />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">No Credit Card Required</p>
                            <p className="text-sm text-slate-500">
                              {trialConfig.no_credit_card_required 
                                ? 'Customers can start trial without payment info' 
                                : 'Payment info required to start trial'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setTrialConfig(prev => ({ ...prev, no_credit_card_required: !prev.no_credit_card_required }))}
                          className="relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                          style={{ backgroundColor: trialConfig.no_credit_card_required ? '#0FAC6C' : '#CBD5E1' }}
                          data-testid="no-cc-required-toggle"
                        >
                          <div 
                            className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200"
                            style={{ transform: trialConfig.no_credit_card_required ? 'translateX(30px)' : 'translateX(2px)' }}
                          />
                        </button>
                      </div>
                      
                      {/* Auto-Upgrade Toggle */}
                      <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <TrendingUp className="text-blue-600" size={20} />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">Auto-Upgrade After Trial</p>
                            <p className="text-sm text-slate-500">
                              {trialConfig.no_credit_card_required 
                                ? 'Disabled when no card is collected' 
                                : 'Automatically upgrade to paid plan when trial ends'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (!trialConfig.no_credit_card_required) {
                              setTrialConfig(prev => ({ ...prev, auto_upgrade_enabled: !prev.auto_upgrade_enabled }));
                            }
                          }}
                          disabled={trialConfig.no_credit_card_required}
                          className={cn(
                            "relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2",
                            trialConfig.no_credit_card_required && "opacity-50 cursor-not-allowed"
                          )}
                          style={{ backgroundColor: trialConfig.auto_upgrade_enabled && !trialConfig.no_credit_card_required ? '#0FAC6C' : '#CBD5E1' }}
                          data-testid="auto-upgrade-toggle"
                        >
                          <div 
                            className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200"
                            style={{ transform: trialConfig.auto_upgrade_enabled && !trialConfig.no_credit_card_required ? 'translateX(30px)' : 'translateX(2px)' }}
                          />
                        </button>
                      </div>
                      
                      {/* Default Paid Plan Selection */}
                      <div>
                        <Label className="text-slate-700 font-medium">Default Paid Plan After Trial</Label>
                        <Select 
                          value={trialConfig.default_paid_plan_id || 'none'}
                          onValueChange={(value) => setTrialConfig(prev => ({ ...prev, default_paid_plan_id: value === 'none' ? null : value }))}
                        >
                          <SelectTrigger className="mt-2" data-testid="default-plan-select">
                            <SelectValue placeholder="Select default plan..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Default (User Chooses)</SelectItem>
                            <SelectItem value="starter">Starter - $29/month</SelectItem>
                            <SelectItem value="pro">Pro - $49/month</SelectItem>
                            <SelectItem value="premium">Premium - $99/month</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Usage Limits Section */}
                      <div className="border border-slate-200 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setTrialConfigExpanded(prev => ({ ...prev, limits: !prev.limits }))}
                          className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                          data-testid="usage-limits-toggle"
                        >
                          <div className="flex items-center gap-3">
                            <Settings className="text-slate-500" size={18} />
                            <span className="font-medium text-slate-700">Usage Limits During Trial</span>
                          </div>
                          {trialConfigExpanded.limits ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        
                        {trialConfigExpanded.limits && (
                          <div className="p-4 space-y-4 border-t border-slate-200">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label className="text-sm text-slate-600">Max Disputes / Month</Label>
                                <Input
                                  type="number"
                                  value={trialConfig.max_disputes_per_month}
                                  onChange={(e) => setTrialConfig(prev => ({ ...prev, max_disputes_per_month: parseInt(e.target.value) || 0 }))}
                                  className="mt-1"
                                  min={0}
                                />
                              </div>
                              <div>
                                <Label className="text-sm text-slate-600">Max Letters / Month</Label>
                                <Input
                                  type="number"
                                  value={trialConfig.max_letters_per_month}
                                  onChange={(e) => setTrialConfig(prev => ({ ...prev, max_letters_per_month: parseInt(e.target.value) || 0 }))}
                                  className="mt-1"
                                  min={0}
                                />
                              </div>
                              <div>
                                <Label className="text-sm text-slate-600">Max AI Analyses</Label>
                                <Input
                                  type="number"
                                  value={trialConfig.max_ai_analyses}
                                  onChange={(e) => setTrialConfig(prev => ({ ...prev, max_ai_analyses: parseInt(e.target.value) || 0 }))}
                                  className="mt-1"
                                  min={0}
                                />
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <Checkbox 
                                  checked={trialConfig.can_import_reports}
                                  onCheckedChange={(checked) => setTrialConfig(prev => ({ ...prev, can_import_reports: checked }))}
                                />
                                <span className="text-sm text-slate-700">Can Import Reports</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <Checkbox 
                                  checked={trialConfig.can_use_credit_builder}
                                  onCheckedChange={(checked) => setTrialConfig(prev => ({ ...prev, can_use_credit_builder: checked }))}
                                />
                                <span className="text-sm text-slate-700">Can Use Credit Builder</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <Checkbox 
                                  checked={trialConfig.can_access_blueprints}
                                  onCheckedChange={(checked) => setTrialConfig(prev => ({ ...prev, can_access_blueprints: checked }))}
                                />
                                <span className="text-sm text-slate-700">Can Access Blueprints</span>
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Reminder Schedule Section */}
                      <div className="border border-slate-200 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setTrialConfigExpanded(prev => ({ ...prev, reminders: !prev.reminders }))}
                          className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                          data-testid="reminders-toggle"
                        >
                          <div className="flex items-center gap-3">
                            <Bell className="text-slate-500" size={18} />
                            <span className="font-medium text-slate-700">Reminder Schedule</span>
                          </div>
                          {trialConfigExpanded.reminders ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        
                        {trialConfigExpanded.reminders && (
                          <div className="p-4 space-y-4 border-t border-slate-200">
                            <p className="text-sm text-slate-600">
                              Send reminder emails at these days before trial expiration:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {[1, 3, 5, 7, 14].map(day => (
                                <button
                                  key={day}
                                  onClick={() => {
                                    const current = trialConfig.reminder_days || [];
                                    if (current.includes(day)) {
                                      setTrialConfig(prev => ({ 
                                        ...prev, 
                                        reminder_days: current.filter(d => d !== day) 
                                      }));
                                    } else {
                                      setTrialConfig(prev => ({ 
                                        ...prev, 
                                        reminder_days: [...current, day].sort((a, b) => b - a) 
                                      }));
                                    }
                                  }}
                                  className={cn(
                                    "px-4 py-2 rounded-lg font-medium text-sm transition-colors",
                                    trialConfig.reminder_days?.includes(day)
                                      ? "bg-emerald-100 text-emerald-700 border-2 border-emerald-300"
                                      : "bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200"
                                  )}
                                >
                                  {day} {day === 1 ? 'day' : 'days'}
                                </button>
                              ))}
                            </div>
                            <p className="text-xs text-slate-500">
                              Currently set: {trialConfig.reminder_days?.length > 0 
                                ? trialConfig.reminder_days.join(', ') + ' days before expiration'
                                : 'No reminders configured'}
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  
                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t border-slate-200">
                    <Button 
                      onClick={handleSaveTrialConfig}
                      disabled={savingTrialConfig}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="save-trial-config-btn"
                    >
                      {savingTrialConfig ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={16} className="mr-2" />
                          Save Configuration
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Info className="text-blue-600" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-blue-800 mb-1">Self-Serve Portal Surface</h4>
                  <p className="text-sm text-blue-700">
                    Customers signing up for this trial will access your portal at{' '}
                    <span className="font-mono bg-blue-100 px-2 py-0.5 rounded">app.fundelitehq.ai</span>.
                    This is your customer-facing Self-Serve / Auto Repair portal, separate from the team/admin workspace.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'services':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Services & Pricing</h2>
                <p className="text-sm text-slate-500">Manage your service catalog and pricing</p>
              </div>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus size={16} className="mr-2" />
                Add Service
              </Button>
            </div>
            
            {/* Service Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'Basic Credit Repair', price: 99, type: 'monthly', description: 'Entry-level credit repair service' },
                { name: 'Standard Package', price: 199, type: 'monthly', description: 'Full dispute service with monitoring' },
                { name: 'Premium Package', price: 299, type: 'monthly', description: 'Comprehensive repair with priority support' },
                { name: 'Setup Fee', price: 49, type: 'one-time', description: 'One-time account setup and analysis' },
              ].map((service, index) => (
                <div key={index} className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Package className="text-emerald-600" size={20} />
                    </div>
                    <Badge variant="outline" className="capitalize">{service.type}</Badge>
                  </div>
                  <h4 className="text-base font-semibold text-slate-900 mb-1">{service.name}</h4>
                  <p className="text-sm text-slate-500 mb-3">{service.description}</p>
                  <p className="text-2xl font-bold text-slate-900">
                    ${service.price}
                    <span className="text-sm font-normal text-slate-500">
                      {service.type === 'monthly' ? '/mo' : ''}
                    </span>
                  </p>
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1">Edit</Button>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
        
      case 'collect':
        return (
          <div className="space-y-6">
            <BillingDisclaimer 
              acknowledged={disclaimerAcknowledged} 
              onAcknowledge={setDisclaimerAcknowledged} 
            />
            
            {!canCollectPayment && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Info className="text-blue-600" size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">Disclosure Required</p>
                  <p className="text-xs text-blue-600">
                    You must acknowledge the Information Disclosure before collecting payments.
                  </p>
                </div>
                <Button onClick={() => setShowDisclosureModal(true)} className="bg-blue-600 hover:bg-blue-700">
                  View Disclosure
                </Button>
              </div>
            )}
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-6">Collect Payment</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Select Client</Label>
                    <Select disabled={!canCollectPayment}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a client..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client1">John Smith</SelectItem>
                        <SelectItem value="client2">Maria Garcia</SelectItem>
                        <SelectItem value="client3">Robert Johnson</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Payment Type</Label>
                    <Select disabled={!canCollectPayment}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="service">Service Payment</SelectItem>
                        <SelectItem value="invoice">Invoice Payment</SelectItem>
                        <SelectItem value="custom">Custom Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Service / Invoice</Label>
                    <Select disabled={!canCollectPayment}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select service or invoice..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basic">Basic Credit Repair - $99/mo</SelectItem>
                        <SelectItem value="standard">Standard Package - $199/mo</SelectItem>
                        <SelectItem value="premium">Premium Package - $299/mo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Amount</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <Input 
                        type="number" 
                        placeholder="0.00" 
                        className="pl-7" 
                        disabled={!canCollectPayment}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="text-sm font-semibold text-slate-700 mb-4">Payment Summary</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Client</span>
                      <span className="text-slate-700">—</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Service</span>
                      <span className="text-slate-700">—</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Amount</span>
                      <span className="text-slate-700">$0.00</span>
                    </div>
                    <hr className="border-slate-200" />
                    <div className="flex justify-between text-base font-semibold">
                      <span className="text-slate-700">Total</span>
                      <span className="text-slate-900">$0.00</span>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700"
                    disabled={!canCollectPayment}
                    onClick={handleCollectPaymentClick}
                  >
                    <CreditCard size={16} className="mr-2" />
                    Proceed to Payment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'invoices':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Client Invoices</h2>
                <p className="text-sm text-slate-500">Create and manage invoices for your clients</p>
              </div>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus size={16} className="mr-2" />
                Create Invoice
              </Button>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Invoice #</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Client</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Due Date</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      { id: 'INV-001', client: 'John Smith', amount: 199, date: '2026-04-01', due: '2026-04-15', status: 'paid' },
                      { id: 'INV-002', client: 'Maria Garcia', amount: 299, date: '2026-04-05', due: '2026-04-19', status: 'pending' },
                      { id: 'INV-003', client: 'Robert Johnson', amount: 149, date: '2026-04-10', due: '2026-04-24', status: 'pending' },
                      { id: 'INV-004', client: 'Sarah Williams', amount: 99, date: '2026-03-15', due: '2026-03-29', status: 'overdue' },
                    ].map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{invoice.id}</td>
                        <td className="px-6 py-4 text-sm text-slate-700">{invoice.client}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">${invoice.amount}</td>
                        <td className="px-6 py-4 text-sm text-slate-500">{invoice.date}</td>
                        <td className="px-6 py-4 text-sm text-slate-500">{invoice.due}</td>
                        <td className="px-6 py-4">
                          <Badge className={cn(
                            'capitalize',
                            invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                            invoice.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          )}>
                            {invoice.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm"><Eye size={14} /></Button>
                            <Button variant="ghost" size="sm"><Send size={14} /></Button>
                            <Button variant="ghost" size="sm"><Download size={14} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
        
      case 'history':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Payment History</h2>
              <p className="text-sm text-slate-500">Complete history of all client payments</p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Client</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Service/Invoice</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Processed By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      { date: '2026-04-12 14:32', client: 'John Smith', service: 'Credit Repair - Month 1', amount: 199, status: 'completed', processor: 'Admin User' },
                      { date: '2026-04-11 10:15', client: 'Maria Garcia', service: 'Premium Package', amount: 299, status: 'completed', processor: 'Admin User' },
                      { date: '2026-04-10 16:45', client: 'Robert Johnson', service: 'Basic Plan', amount: 149, status: 'completed', processor: 'Staff Member' },
                      { date: '2026-04-09 09:20', client: 'Sarah Williams', service: 'Setup Fee', amount: 99, status: 'pending', processor: 'Admin User' },
                      { date: '2026-04-08 11:30', client: 'James Brown', service: 'Standard Package', amount: 199, status: 'refunded', processor: 'Admin User' },
                    ].map((payment, index) => (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm text-slate-500">{payment.date}</td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{payment.client}</td>
                        <td className="px-6 py-4 text-sm text-slate-700">{payment.service}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">${payment.amount}</td>
                        <td className="px-6 py-4">
                          <Badge className={cn(
                            'capitalize',
                            payment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            payment.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            payment.status === 'refunded' ? 'bg-blue-100 text-blue-700' :
                            'bg-red-100 text-red-700'
                          )}>
                            {payment.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">{payment.processor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
        
      case 'refunds':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Refunds & Adjustments</h2>
                <p className="text-sm text-slate-500">Process refunds and payment adjustments</p>
              </div>
              <Button variant="outline">
                <Plus size={16} className="mr-2" />
                New Refund Request
              </Button>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Client</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Original Amount</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Refund Amount</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Reason</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      { date: '2026-04-08', client: 'James Brown', original: 199, refund: 199, reason: 'Service cancellation', status: 'completed' },
                      { date: '2026-04-02', client: 'Emily Davis', original: 99, refund: 50, reason: 'Partial refund - discount', status: 'completed' },
                    ].map((refund, index) => (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm text-slate-500">{refund.date}</td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{refund.client}</td>
                        <td className="px-6 py-4 text-sm text-slate-700">${refund.original}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-red-600">-${refund.refund}</td>
                        <td className="px-6 py-4 text-sm text-slate-500">{refund.reason}</td>
                        <td className="px-6 py-4">
                          <Badge className="bg-emerald-100 text-emerald-700 capitalize">{refund.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
        
      case 'compliance':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Compliance Disclosure</h2>
              <p className="text-sm text-slate-500">View disclosure requirements and acknowledgment history</p>
            </div>
            
            <BillingDisclaimer 
              acknowledged={disclaimerAcknowledged} 
              onAcknowledge={setDisclaimerAcknowledged} 
            />
            
            {/* Disclosure Text */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Shield className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Information Disclosure</h3>
                  <p className="text-sm text-slate-500">Required acknowledgment for payment collection</p>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-5 mb-4">
                <div className="prose prose-sm max-w-none text-slate-700">
                  <p>
                    To be fully transparent, the FundElite platform wants you to understand that collecting 
                    payments for credit repair services may be subject to federal and state legal requirements.
                  </p>
                  <p>Before collecting payment from your customers, you should review and understand:</p>
                  <ul>
                    <li>applicable state law</li>
                    <li>the Credit Repair Organizations Act (CROA)</li>
                    <li>the Telemarketing Sales Rule (TSR), if telemarketing or phone sales are involved</li>
                  </ul>
                  <p>
                    By proceeding, you acknowledge that you are responsible for reviewing and complying 
                    with the laws applicable to your business and payment practices.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <p className="text-slate-500">Disclosure Version: <span className="font-medium text-slate-700">{DISCLOSURE_VERSION}</span></p>
                  <p className="text-slate-500">Last Updated: <span className="font-medium text-slate-700">April 2026</span></p>
                </div>
                <div className="flex items-center gap-3">
                  {disclosureAcknowledged ? (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle2 size={14} className="mr-1" />
                      Acknowledged
                    </Badge>
                  ) : (
                    <Button onClick={() => setShowDisclosureModal(true)} className="bg-blue-600 hover:bg-blue-700">
                      Acknowledge Disclosure
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Acknowledgment History */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Acknowledgment History</h3>
              
              {acknowledgmentHistory.length > 0 ? (
                <div className="space-y-3">
                  {acknowledgmentHistory.map((ack, index) => (
                    <div key={index} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="text-emerald-500" size={18} />
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            Version {ack.disclosure_version} acknowledged
                          </p>
                          <p className="text-xs text-slate-500">
                            by {ack.user_name || ack.user_id} • {ack.context}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-slate-500">{ack.timestamp}</span>
                    </div>
                  ))}
                </div>
              ) : disclosureAcknowledged ? (
                <div className="flex items-center gap-3 py-3">
                  <CheckCircle2 className="text-emerald-500" size={18} />
                  <p className="text-sm text-slate-700">Disclosure has been acknowledged for current version.</p>
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">No acknowledgment records yet.</p>
              )}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div data-testid="client-billing-page">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 mb-6">
        <div className="px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <DollarSign className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Client Billing</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Collect payments from your clients
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {disclosureAcknowledged && (
              <Badge className="bg-emerald-100 text-emerald-700 border-0">
                <CheckCircle2 size={14} className="mr-1" />
                Compliance Acknowledged
              </Badge>
            )}
            <Button 
              onClick={handleCollectPaymentClick}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CreditCard size={16} className="mr-2" />
              Collect Payment
            </Button>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="px-6 border-t border-slate-200">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                    activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  )}
                  data-testid={`billing-tab-${tab.id}`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>
      
      {/* Tab Content */}
      {renderTabContent()}
      
      {/* Information Disclosure Modal */}
      <InformationDisclosureModal
        open={showDisclosureModal}
        onClose={() => setShowDisclosureModal(false)}
        onAcknowledge={handleAcknowledgeDisclosure}
        loading={acknowledgingDisclosure}
      />
    </div>
  );
};

export default ClientBillingPage;
